import React from 'react';

import { SchedulePauseContext, SchedulePauseReason } from 'utils/api/modules/schedules/schedules.types';
import { UuidableName } from 'utils/api/types';
import { formatMomentDate } from 'utils/formatters/timezone';

import { BannerContainer, BannerMessage } from './styles';

interface Props {
  pauseReason: SchedulePauseReason;
  pausedAt: string | null;
  pausedBy: UuidableName | null;
  pauseContext: SchedulePauseContext | null;
}

const joinGroupNames = (names: string[]): string =>
  names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}` : names[0];

const PauseBanner: React.FunctionComponent<Props> = ({ pauseReason, pausedAt, pausedBy, pauseContext }) => {
  const formattedDate = pausedAt ? formatMomentDate(pausedAt, 'DD MMM YYYY') : null;

  const message = ((): string => {
    if (pauseReason === SchedulePauseReason.MANUAL) {
      const datePart = formattedDate ? `Paused on ${formattedDate}` : 'Paused';
      return pausedBy?.name ? `${datePart} by ${pausedBy.name}.` : `${datePart}.`;
    }

    const prefix = formattedDate ? `Auto-paused on ${formattedDate}.` : 'Auto-paused.';

    if (pauseReason === SchedulePauseReason.NO_ACTIVE_ASSIGNEES) {
      const groupNames = pauseContext?.groupNames;
      if (groupNames?.length) {
        return `${prefix} No one in ${joinGroupNames(groupNames)} performs this schedule any more. Add an active assignee or group to resume.`;
      }
      const assigneeName = pauseContext?.assigneeName;
      const middle = assigneeName ? `Sole assignee ${assigneeName} was deactivated.` : 'Sole assignee was deactivated.';
      return `${prefix} ${middle} Add an active assignee or group to resume.`;
    }

    if (pauseReason === SchedulePauseReason.TEMPLATE_ARCHIVED) {
      const templateName = pauseContext?.templateName;
      const middle = templateName
        ? `Linked Audit Template '${templateName}' was archived.`
        : 'Linked Audit Template was archived.';
      return `${prefix} ${middle} Select an active Audit Template to resume.`;
    }

    return prefix;
  })();

  return (
    <BannerContainer role="status">
      <BannerMessage>{message}</BannerMessage>
    </BannerContainer>
  );
};

export default PauseBanner;
